<script lang="ts" generics="Item">
	import type { Snippet } from "svelte";
	import type { TouchEventHandler, WheelEventHandler } from "svelte/elements";

	let {
		// eslint-disable-next-line prefer-const
		items,
		// eslint-disable-next-line prefer-const
		item,
		activeIndex = $bindable(0),
		canNext = $bindable(true),
		canPrev = $bindable(true),
	}: {
		items: Item[];
		item: Snippet<[item: Item, index: number]>;
		activeIndex?: number;
		canNext?: boolean;
		canPrev?: boolean;
	} = $props();

	let direction = $state<"next" | "prev">();
	let animatingIndex = $state<number>();
	let hiddenIndex = $state<number>();
	const isAnimationRunning = $derived(animatingIndex !== undefined);

	function clearAfterTransition(e: TransitionEvent): void {
		if (!direction || e.propertyName !== "transform") {
			return;
		}
		if (direction === "prev") {
			hiddenIndex = animatingIndex;
		}
		direction = undefined;
		animatingIndex = undefined;
	}

	export function next(): void {
		const isSmallScreen = typeof window !== "undefined" && window.innerWidth < 640;
		const minIndex = isSmallScreen ? 0 : 1;

		if (activeIndex > minIndex && !isAnimationRunning) {
			animatingIndex = activeIndex--;
			direction = "prev";
		}
	}

	let root = $state<HTMLDivElement>();

	export function prev(): void {
		if (activeIndex < items.length - 1 && !isAnimationRunning) {
			hiddenIndex = undefined;
			direction = "next";
			animatingIndex = ++activeIndex;
		}
	}

	const desktopScrollHandler: WheelEventHandler<Window> = (event) => {
		if (!root?.matches(":hover") || !event.shiftKey || event.ctrlKey || isAnimationRunning) {
			return;
		}
		if (event.deltaY > 0) {
			next();
		} else {
			prev();
		}
	};

	const mobileScrollHandler: TouchEventHandler<HTMLDivElement> = (event) => {
		const [touch, ...touches] = event.touches;
		if (!touch || touches.length > 0) {
			return;
		}
		const { clientX: xDown, clientY: yDown } = touch;
		document.addEventListener(
			"touchmove",
			(event) => {
				const [touch, ...touches] = event.touches;
				if (!touch || touches.length > 0) {
					return;
				}
				const { clientX: xUp, clientY: yUp } = touch;
				const xDiff = xDown - xUp;
				const yDiff = yDown - yUp;

				if (Math.abs(xDiff) > Math.abs(yDiff)) {
					if (xDiff > 0) {
						next();
					} else {
						prev();
					}
				}
			},
			{ once: true },
		);
	};

	$effect(() => {
		const isSmallScreen = typeof window !== "undefined" && window.innerWidth < 640;
		const minIndex = isSmallScreen ? 0 : 1;

		canNext = activeIndex > minIndex;
		canPrev = activeIndex < items.length - 1;
	});
</script>

<svelte:window onwheel={desktopScrollHandler} />

<div
	class="relative w-full h-[20.875rem] md:h-[25rem] xl:h-[34rem]"
	bind:this={root}
	ontouchstart={mobileScrollHandler}
>
	<div class="relative size-full">
		{#each items as it, i (i)}
			{@const slot = activeIndex - i}
			{@const isAnimatingNext = direction === "next" && i === animatingIndex}
			{@const isAnimatingPrev = direction === "prev" && i === animatingIndex}
			{@const isAnimating = isAnimatingNext || isAnimatingPrev}
			{@const fixedSlot = isAnimatingPrev ? 0 : slot}
			{@const shouldHide = i === hiddenIndex}
			{#if !shouldHide}
				<div
					class={[
						"absolute top-0 left-0 h-full will-change-transform carousel-slide max-w-[16rem] xl:max-w-[32rem]",
						!isAnimating && "transition-all duration-700 ease-[cubic-bezier(0.34,0.3,0.4,1)]",
						slot < 0 && "pointer-events-none",
						!isAnimatingPrev && fixedSlot < 0 && "opacity-0",
					]}
					style:transform="translateX({fixedSlot >= 0 ? fixedSlot * 100 : -100}%)"
					style:z-index={isAnimating ? 5 : 10 + (items.length - slot)}
					ontransitionend={clearAfterTransition}
				>
					<div
						class="size-full origin-center overflow-hidden rounded-3xl"
						class:animate-pop={isAnimatingNext}
						class:animate-shrink={isAnimatingPrev}
					>
						{@render item(it, i)}
					</div>
				</div>
			{/if}
		{/each}
	</div>
</div>

<style>
	.carousel-slide {
		width: 256px;
		min-width: 256px;
	}

	@media (min-width: 640px) {
		.carousel-slide {
			width: 60%;
			min-width: auto;
		}
	}

	@media (min-width: 1024px) {
		.carousel-slide {
			width: max(512px, 40%);
		}
	}

	@keyframes pop {
		0% {
			transform: scale(0);
			opacity: 0;
		}
		100% {
			transform: scale(1);
			opacity: 1;
		}
	}

	@keyframes shrink {
		0% {
			transform: scale(1);
			opacity: 1;
		}
		50% {
			transform: scale(0.4);
			opacity: 0.1;
		}
		75% {
			transform: scale(0.2);
			opacity: 0;
		}
		100% {
			transform: scale(0);
			opacity: 0;
		}
	}

	.animate-pop {
		animation: pop 0.7s cubic-bezier(0.34, 0.3, 0.4, 1) forwards;
	}

	.animate-shrink {
		animation: shrink 0.7s cubic-bezier(0.34, 0.3, 0.4, 1) forwards;
	}
</style>
